import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useNespresso } from "../context/NespressoContext";

// Nespresso brand colors
const NESPRESSO = {
  brown: "#986f38",
  brownHover: "#815e2f",
  gold: "#c69f36",
  black: "#000000",
  darkGray: "#292929",
  lightGray: "#f9f9f9",
  border: "#e6e5e4",
};

// Per spec: Each pod = 6g, 1 point per pod
const GRAMS_PER_POD = 6;

// Demo weight simulation (in grams) - will be converted to pods
const DEMO_WEIGHTS = [180, 240, 120, 300]; // Results in 30, 40, 20, 50 pods

interface WeighedBag {
  weightGrams: number;
  podCount: number;
  bagNumber: number;
}

const NespressoScanView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useNespresso();
  const [weighedBags, setWeighedBags] = useState<WeighedBag[]>([]);
  const [isWeighing, setIsWeighing] = useState(false);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [displayedPoints, setDisplayedPoints] = useState(0);
  const [currentBagIndex, setCurrentBagIndex] = useState(0);

  // Calculate totals - 1 point per pod
  const totalPods = weighedBags.reduce((sum, bag) => sum + bag.podCount, 0);
  const totalPoints = totalPods; // 1 point per pod per spec
  const totalWeight = weighedBags.reduce((sum, bag) => sum + bag.weightGrams, 0);

  // Animated points counter
  useEffect(() => {
    if (displayedPoints < totalPoints) {
      const timer = setTimeout(() => {
        const increment = Math.max(1, Math.ceil((totalPoints - displayedPoints) / 10));
        setDisplayedPoints((prev) => Math.min(totalPoints, prev + increment));
      }, 30);
      return () => clearTimeout(timer);
    }
  }, [displayedPoints, totalPoints]);

  const simulateWeighing = useCallback(() => {
    if (currentBagIndex >= DEMO_WEIGHTS.length) return;

    setIsWeighing(true);
    setCurrentWeight(0);

    const targetWeight = DEMO_WEIGHTS[currentBagIndex];
    const steps = 20;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setCurrentWeight(targetWeight);
        clearInterval(interval);

        // Calculate pods from weight
        const podCount = Math.round(targetWeight / GRAMS_PER_POD);

        const newBag: WeighedBag = {
          weightGrams: targetWeight,
          podCount,
          bagNumber: currentBagIndex + 1,
        };

        setWeighedBags((prev) => [...prev, newBag]);

        // Add to Nespresso gamification context
        dispatch({
          type: "INCREMENT_LIFETIME_PODS",
          payload: podCount,
        });

        setCurrentBagIndex((prev) => prev + 1);
        setIsWeighing(false);
      } else {
        // Gradual weight increase with some variation
        const progress = currentStep / steps;
        const variation = (Math.random() - 0.5) * 10;
        setCurrentWeight(Math.floor(targetWeight * progress + variation));
      }
    }, 80);

    return () => clearInterval(interval);
  }, [currentBagIndex, dispatch]);

  // Redeem - proceed to label and deposit (Step 4)
  const handleRedeem = () => {
    navigate("/nespresso/confirmation");
  };

  // Cancel - return to Step 0 resting screen
  const handleCancel = () => {
    // Reset the gamification state since we're canceling
    dispatch({ type: "RESET_GAMIFICATION" });
    navigate("/nespresso");
  };

  const handleBack = () => {
    navigate("/nespresso/takeback");
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <span className="font-medium uppercase tracking-widest text-sm" style={{ color: NESPRESSO.gold }}>
          Nespresso Recycling Program
        </span>
        <div className="text-white/60 font-light">
          Points{" "}
          <motion.span
            key={displayedPoints}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            className="font-medium"
            style={{ color: NESPRESSO.gold }}
          >
            {displayedPoints}
          </motion.span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        {/* Animated Points Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="text-white/60 text-sm mb-2">Your points</p>
          <motion.div
            className="text-7xl font-light"
            style={{ color: NESPRESSO.gold }}
            key={displayedPoints}
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
          >
            {displayedPoints}
          </motion.div>
          <p className="text-white/40 text-sm mt-2">
            {totalPods} capsules • {totalWeight}g
          </p>
        </motion.div>

        {/* Scale/Weighing Area */}
        <div className="w-full max-w-md mb-8">
          <div
            className="bg-white/5 p-8 flex flex-col items-center justify-center"
            style={{ minHeight: "200px" }}
          >
            {isWeighing ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                <div className="w-32 h-32 relative mb-4 mx-auto">
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: `${NESPRESSO.gold}15` }}
                    animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <div
                    className="absolute inset-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${NESPRESSO.gold}20` }}
                  >
                    <motion.span
                      className="text-2xl font-medium"
                      style={{ color: NESPRESSO.gold }}
                      key={currentWeight}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                    >
                      {currentWeight}g
                    </motion.span>
                  </div>
                </div>
                <p className="text-white font-light">Weighing bag...</p>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
                <div className="w-24 h-24 bg-white/5 flex items-center justify-center mb-4 mx-auto rounded-full">
                  <svg className="w-12 h-12 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <p className="text-white/60 mb-4 font-light">
                  {currentBagIndex >= DEMO_WEIGHTS.length
                    ? "All demo bags weighed!"
                    : "Place bag on scale"}
                </p>
                {currentBagIndex < DEMO_WEIGHTS.length && (
                  <button
                    onClick={simulateWeighing}
                    className="px-8 py-3 text-white font-medium transition-colors"
                    style={{ backgroundColor: NESPRESSO.brown, borderRadius: "3px" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = NESPRESSO.brownHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = NESPRESSO.brown)}
                  >
                    Simulate Bag {currentBagIndex + 1}
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Weighed Bags List */}
        {weighedBags.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md mb-8"
          >
            <div className="space-y-2">
              <AnimatePresence>
                {weighedBags.map((bag) => (
                  <motion.div
                    key={bag.bagNumber}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="bg-white/5 px-4 py-3 flex items-center justify-between"
                  >
                    <span className="text-white/60">Bag {bag.bagNumber}</span>
                    <span className="text-white/40">{bag.weightGrams}g</span>
                    <span className="text-white font-medium">{bag.podCount} pods</span>
                    <span style={{ color: NESPRESSO.gold }} className="font-medium">
                      +{bag.podCount} pts
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}

        {/* Redeem / Cancel Buttons (per spec) */}
        {weighedBags.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-4"
          >
            <button
              onClick={handleCancel}
              className="px-8 py-4 text-white/60 hover:text-white font-medium transition-colors border border-white/20 hover:border-white/40"
              style={{ borderRadius: "3px" }}
            >
              Cancel
            </button>
            <motion.button
              onClick={handleRedeem}
              className="px-12 py-4 text-white font-medium transition-colors"
              style={{ backgroundColor: NESPRESSO.brown, borderRadius: "3px" }}
              whileHover={{ scale: 1.02, backgroundColor: NESPRESSO.brownHover }}
              whileTap={{ scale: 0.98 }}
            >
              Redeem {totalPoints} Points
            </motion.button>
          </motion.div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-3 px-8 text-center text-white/30 text-xs border-t border-white/5 font-light">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default NespressoScanView;
